import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import InputField from "components/Input";
import SelectField from "components/Select";
import ClientServices from "services/Client";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import { useWatch } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";

const CostingDialog = ({
  open,
  onClose,
  containerData,
  costings,
  currencyExchangeRate,
  navigate,
  selectedClient,
  setSelectedClientBooking,
  findDifferences,
  getClientCosting,
  copyClientCosting,
  selectedClientBooking,
  savedData,
  setSavedData,
  setSelectedContainer,
  handleCalc,
  readOnly,
  bookingDiscountFlags,
}) => {
  const [selectedVin, setSelectedVin] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoSelected, setAutoSelected] = useState(false);
  const {
    control,
    handleSubmit,
    setValue,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm();
  const vinOptions = costings.map((item) => ({
    id: item.booking.vin,
    name: item.booking.vin,
  }));

  const orderedFields = [
    "shipping_charges",
    "shipping_charges_2",
    "late_fee",
    "ship_late_fee",
    "storage",
    "terminal_storages",
    "category_a",
    "broker_fee",
    "title_fee",
    "current_suv",
    "clearance_att_fee",
    "hybrid_fee",
    "heavy_equipment_fee",
    "parts_pickup_fee",
    "wrapping_fee",
    "relist_reversal_fee",
    "misc_fee",
    "fuel_surcharge",
    "color_change",
    "thc_charges",
    "discount",
    "war_surcharge_disc",
    "total_costing",
  ];

  const fieldLabels = {
    misc_fee: "WAR RISK Surcharge",
    late_fee: "Auction Late Pmt Fee",
    ship_late_fee: "Ship. Late Pmt Fee",
    war_surcharge_disc: "War Sur.Char Disc",
  };

  const getFieldLabel = (key) =>
    fieldLabels[key] || key.replace(/_/g, " ").toUpperCase();

  const HIGH_VALUE_VEHICLE_THRESHOLD = 50000;

  const defaultFormData = orderedFields.reduce(
    (acc, field) => ({ ...acc, [field]: 0 }),
    {}
  );

  const handleVinChange = (newVin) => {
    if (selectedVin) {
      const currentData = watch();
      setSavedData((prev) => ({
        ...prev,
        [selectedVin]: {
          ...(prev[selectedVin] || {}),
          ...currentData,
        },
      }));
    }
    setTimeout(() => {
      setSelectedVin(newVin?.id);
    }, 50);
  };

  const totalCostingValue = useWatch({ control, name: "total_costing" });

  const isSubmitEnabled = parseFloat(totalCostingValue || 0) > 0;

  useEffect(() => {
    if (selectedVin) {
      if (savedData[selectedVin]) {
        reset(savedData[selectedVin]);
      } else {
        const selectedData = costings.find(
          (item) => item.booking.vin === selectedVin
        );
        if (selectedData) {
          // const formattedData = orderedFields.reduce((acc, key) => ({
          //     ...acc,
          //     [key]: selectedData[key] || 0
          // }), {});
          const vehicleValue =
            parseFloat(selectedData?.booking?.value || 0) +
            parseFloat(selectedData?.booking?.other_charges || 0);
          const isHighValueVehicle =
            vehicleValue >= HIGH_VALUE_VEHICLE_THRESHOLD;

          const formattedData = orderedFields.reduce((acc, key) => {
            let value = selectedData[key] || 0;

            if (key === "discount") {
              const selectedDiscount =
                selectedData?.discount !== "0.000"
                  ? selectedData?.discount
                  : selectedData?.booking?.user_disc;
              value = selectedDiscount || 0;
            }

            if (key === "shipping_charges_2") {
              const savedValue = parseFloat(
                selectedData?.shipping_charges_2 || 0
              );
              if (savedValue > 0) {
                value = savedValue;
              } else if (isHighValueVehicle) {
                value = parseFloat(selectedData?.shipping_charges || 0);
              } else {
                value = 0;
              }
            }

            return {
              ...acc,
              [key]: parseFloat(value).toFixed(2),
            };
          }, {});

          formattedData.total_costing = orderedFields
            .filter((field) => field !== "total_costing" && field !== "discount")
            .reduce(
              (sum, field) =>
                field === "war_surcharge_disc"
                  ? sum - parseFloat(formattedData[field] || 0)
                  : sum + parseFloat(formattedData[field] || 0),
              0
            )
            .toFixed(2);

          reset(formattedData);
        } else {
          reset(defaultFormData);
        }
      }
    }
  }, [selectedVin, costings, reset, savedData]);

  const updateClientCostingg = async () => {
    if (!selectedVin) {
      alert("No VIN selected.");
      return;
    }

    const currentData = watch();
    const updatedSavedData = {
      ...savedData,
      [selectedVin]: {
        ...(savedData[selectedVin] || {}),
        ...currentData,
      },
    };

    setSavedData(updatedSavedData);

    setTimeout(async () => {
      setLoading(true);

      try {
        const costingsArr = Object.entries(updatedSavedData)
          .map(([vin, formData]) => {
            const costingDetail = costings.find((e) => e.booking.vin === vin);
            if (!costingDetail) return null;

            return {
              costing_id: costingDetail.id,
              booking_id: costingDetail.booking_id,
              customer_id: costingDetail.booking.customer_id,
              customer_phone: costingDetail.booking.customer?.uae_phone,
              container_no: costingDetail.shipping.container_no,
              vehicle_make: costingDetail.booking.veh_make?.name,
              vehicle_model: costingDetail.booking.veh_model?.name,
              vin,
              lot_number: costingDetail.booking.lot_number,
              color: costingDetail.booking.color,
              loading_port: costingDetail.shipping.loading_port?.name,
              location: costingDetail.shipping.location
                ? `${costingDetail.shipping.location.state_code}-${costingDetail.shipping.location.city_name}`
                : null,
              country_code:
                costingDetail.shipping.location?.country_code || null,
              destination: costingDetail.shipping.dest?.name,
              galaxy_arrival_date: costingDetail.vehicle.arrived_galaxy_date,
              port_date: costingDetail?.vehicle?.arrived_port_date || null,
              custom_duty: parseFloat(
                costingDetail.vehicle.vcc?.custom_due
                  ? costingDetail.custom_duty == 0
                    ? costingDetail.vehicle.vcc.custom_charges_aed /
                      currencyExchangeRate
                    : costingDetail.custom_duty
                  : 0
              ),
              vat: parseFloat(
                costingDetail.vehicle.vcc?.vat_due
                  ? costingDetail.vat == 0
                    ? costingDetail.vehicle.vcc.vat_charges_aed /
                      currencyExchangeRate
                    : costingDetail.vat
                  : 0
              ),
              vendor_type: selectedClient?.type,
              vendor_account_id: selectedClient?.account_id,
              discount_aed: (
                (parseFloat(formData?.discount || 0) || 0) *
                (parseFloat(currencyExchangeRate || 0) || 0)
              ).toFixed(2),
              ...formData,
            };
          })
          .filter(Boolean);

        await ClientServices.updateClientCostingV2({ costings: costingsArr });

        getClientCosting();
        SuccessToaster("Costing updated successfully!");
        setSelectedClientBooking([]);
        setSelectedContainer(null);
        navigate("/shipping-costing-v2");
        onClose();
      } catch (error) {
        ErrorToaster(error);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const fieldNames = orderedFields.filter((f) => f !== "total_costing");

  // Watch only required fields to avoid infinite re-renders
  const watchedFieldValues = useWatch({
    control,
    name: fieldNames,
  });

  useEffect(() => {
    if (!watchedFieldValues || watchedFieldValues.length !== fieldNames.length)
      return;

    let total = 0;

    for (let i = 0; i < fieldNames.length; i++) {
      const field = fieldNames[i];
      const value = parseFloat(watchedFieldValues[i]) || 0;
      if (field === "discount") {
        continue;
      }
      if (field === "war_surcharge_disc") {
        total -= value;
      } else {
        total += value;
      }
    }

    const newTotal = parseFloat(total.toFixed(2));
    const currentTotal = parseFloat(watch("total_costing")) || 0;

    if (newTotal !== currentTotal) {
      setValue("total_costing", newTotal.toFixed(2), {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  }, [watchedFieldValues, watch, setValue]);

  useEffect(() => {
    if (open && !autoSelected && vinOptions.length > 0) {
      const firstVin = vinOptions[0];
      handleVinChange(firstVin); // call your existing change logic
      setAutoSelected(true);
    }
  }, [open, autoSelected, vinOptions]);

  useEffect(() => {
    if (!open) {
      setAutoSelected(false); // reset for next open
      setSelectedVin(""); // optional: clear previous VIN
    }
  }, [open]);

  console.log(costings, "costings");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <strong>Container # {containerData.container_no}</strong>
        <Typography
          variant="body2"
          sx={{ mt: 1, color: "#d32f2f", fontWeight: 800 }}
        >
          Starting from 27th April, Costing Discount will be generated separately.
        </Typography>
        <CloseIcon
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} marginBottom={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SelectField
              size="small"
              label={"Select VIN"}
              options={vinOptions}
              selected={selectedVin}
              onSelect={handleVinChange}
              error={errors?.vin?.message}
            />
          </Grid>
        </Grid>
        {selectedVin &&
          costings.some(
            (c) => c.booking?.vin === selectedVin && c.shipping?.is_consignment
          ) &&
          (() => {
            const matchedContainers = [
              ...new Set(
                costings
                  .filter(
                    (c) =>
                      c.booking?.vin === selectedVin &&
                      c.shipping?.is_consignment
                  )
                  .map((c) => c.shipping?.container_no)
              ),
            ];

            const isSingle = matchedContainers.length === 1;

            return (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  mt: 1,
                  fontFamily: "Helvetica",
                  fontWeight: 700,
                  padding: "10px",
                  fontSize: "16px",
                  color: "#FE2E1B",
                }}
              >
                Please do the costing for the full container.{" "}
                {isSingle
                  ? "This is a consignment container: "
                  : "These are consignment containers: "}
                {matchedContainers.join(", ")}
              </Typography>
            );
          })()}

        {selectedVin && (() => {
          const selectedCostingData = costings.find(
            (item) => item.booking?.vin === selectedVin
          );
          const selectedVehicleValue =
            parseFloat(selectedCostingData?.booking?.value || 0) +
            parseFloat(selectedCostingData?.booking?.other_charges || 0);
          const showShippingCharges2 =
            selectedVehicleValue >= HIGH_VALUE_VEHICLE_THRESHOLD;

          return (
          <form onSubmit={handleSubmit(updateClientCostingg)}>
            <Grid container spacing={2} marginTop={2}>
              {orderedFields.map((key) => {
                if (key === "shipping_charges_2" && !showShippingCharges2) {
                  return null;
                }
                return (
                <Grid item xs={6} sm={3} key={key}>
                  <Controller
                    name={key}
                    control={control}
                    rules={{
                      pattern: {
                        value: /^\d+(\.\d{0,2})?$/, // This is your numberRegex
                        message: "Only numbers allowed, up to 2 decimals", // error message
                      },
                    }}
                    render={({ field }) => {
                      // let value = field.value || 0;
                      // value = parseFloat(value);
                      return (
                        <InputField
                          size="small"
                          type="number"
                          label={getFieldLabel(key)}
                          value={field?.value || ""}
                          onInput={(e) => {
                            const rawValue = e.target.value;
                            const parsedValue = parseFloat(rawValue) || 0;

                            field.onChange(rawValue);

                            if (key !== "total_costing") {
                              const currentValues = watch();
                              const updatedValues = {
                                ...currentValues,
                                [key]: parsedValue,
                              };

                              const total = orderedFields
                                .filter((f) => f !== "total_costing" && f !== "discount")
                                .reduce((sum, f) => {
                                  const val = parseFloat(updatedValues[f]) || 0;
                                  return f === "war_surcharge_disc"
                                    ? sum - val
                                    : sum + val;
                                }, 0);

                              setValue("total_costing", total.toFixed(2), {
                                shouldValidate: false,
                                shouldDirty: true,
                              });
                            }
                          }}
                          error={errors[key]?.message}
                          disabled={
                            readOnly ||
                            key === "total_costing" ||
                            key === "late_fee" ||
                            key === "storage"
                          }
                        />
                      );
                    }}
                  />
                </Grid>
                );
              })}
            </Grid>
            {!readOnly && (
              <DialogActions>
                <Button
                  sx={{
                    textTransform: "capitalize",
                    boxShadow: "none",
                    minWidth: { xs: "100px", md: "150px" },
                    py: 1.2,
                    fontSize: { xs: 12, md: 14 },
                  }}
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !isSubmitEnabled}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </DialogActions>
            )}

            {/* <DialogActions>
                            <Button sx={{
                                textTransform: "capitalize",
                                boxShadow: "none",
                                minWidth: { xs: "100px", md: "150px" },
                                py: 1.2,
                                fontSize: { xs: 12, md: 14 },
                            }} type="submit" color="primary" variant="contained" disabled={loading}>
                                {loading ? "Submitting..." : "Submit"}
                            </Button>
                        </DialogActions> */}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1, textAlign: "left" }}
              >
                The Discount applied is for True Value:
              </Typography>

              <List dense sx={{ pl: 2, textAlign: "left" }}>
                {(() => {
                  const selectedCosting = costings.find(
                    (item) => item.booking?.vin === selectedVin
                  );
                  const flags = selectedCosting
                    ? bookingDiscountFlags?.[selectedCosting?.id] || {}
                    : {};

                  const entries = Object.keys(flags);
                  if (entries.length === 0) {
                    return (
                      <ListItem disablePadding>
                        <ListItemText primary="No discount flags applied." />
                      </ListItem>
                    );
                  }

                  return entries.map((key) => (
                    <ListItem key={key} disablePadding>
                      <ListItemText primary={key.replace(/_/g, " ")} />
                    </ListItem>
                  ));
                })()}
              </List>
            </Box>
          </form>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};

export default CostingDialog;
